import React, { useEffect, useMemo, useState } from "react";
import {
    Loader2,
    Package,
    AlertTriangle,
    RefreshCw,
    Save,
    Plus,
    Minus,
    History,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTime, formatPrice } from "../lib/orderStatus";

export default function AdminStock({ apiAuth }) {
    const [stocks, setStocks] = useState([]);
    const [movements, setMovements] = useState([]);
    const [busy, setBusy] = useState(true);
    const [showMovements, setShowMovements] = useState(false);
    const [adjustingId, setAdjustingId] = useState(null);

    const load = async () => {
        setBusy(true);
        try {
            const [sRes, mRes] = await Promise.all([
                apiAuth("/admin/stocks"),
                apiAuth("/admin/stock-movements"),
            ]);
            setStocks(sRes.data || []);
            setMovements(mRes.data || []);
        } catch {
            toast.error("Erro ao carregar stock");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lowCount = useMemo(() => stocks.filter((s) => s.is_low).length, [stocks]);
    const totalUnits = useMemo(
        () => stocks.reduce((acc, s) => acc + (s.quantity || 0), 0),
        [stocks],
    );
    const inventoryValue = useMemo(
        () => stocks.reduce((acc, s) => acc + (s.quantity || 0) * (s.price || 0), 0),
        [stocks],
    );

    const handleSet = async (productId, { quantity, low_threshold }) => {
        try {
            await apiAuth(`/admin/stocks/${productId}`, {
                method: "PUT",
                data: { quantity, low_threshold },
            });
            await load();
            toast.success("Stock atualizado");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        }
    };

    const handleAdjust = async (productId, delta, reason, note) => {
        try {
            await apiAuth(`/admin/stocks/${productId}/adjust`, {
                method: "POST",
                data: { delta, reason, note: note || null },
            });
            await load();
            setAdjustingId(null);
            toast.success("Movimento registado");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro");
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-stock-panel">
            {/* Stats */}
            <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Stat label="SKUs" value={stocks.length} bg="bg-white" accent="text-brand-green" icon={Package} testid="stock-stat-skus" />
                <Stat label="Unidades em stock" value={totalUnits} bg="bg-brand-green" accent="text-white" icon={Package} white testid="stock-stat-units" />
                <Stat
                    label="Valor em inventário"
                    value={formatPrice(inventoryValue)}
                    bg="bg-brand-ink"
                    accent="text-white"
                    icon={Package}
                    white
                    testid="stock-stat-value"
                />
                <Stat
                    label="Stock baixo"
                    value={lowCount}
                    bg={lowCount > 0 ? "bg-red-50 ring-red-200" : "bg-brand-cream"}
                    accent={lowCount > 0 ? "text-red-600" : "text-brand-ink-soft"}
                    icon={AlertTriangle}
                    testid="stock-stat-low"
                />
            </section>

            <div className="flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={load}
                    data-testid="admin-stock-refresh"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-cream hover:bg-brand-green-soft text-brand-ink px-3 py-2 text-xs font-semibold transition-colors"
                >
                    <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Atualizar
                </button>
                <button
                    type="button"
                    onClick={() => setShowMovements((v) => !v)}
                    data-testid="admin-stock-toggle-movements"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink text-white px-3 py-2 text-xs font-semibold"
                >
                    <History className="h-3.5 w-3.5" />
                    {showMovements ? "Ocultar histórico" : "Ver histórico de movimentos"}
                </button>
            </div>

            {busy ? (
                <div className="p-16 flex justify-center bg-white rounded-2xl shadow-soft">
                    <Loader2 className="h-6 w-6 animate-spin text-brand-green" />
                </div>
            ) : (
                <div className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden">
                    <div className="px-6 py-4 border-b border-brand-green/10">
                        <h3 className="font-display font-medium text-lg text-brand-ink">
                            Inventário ({stocks.length})
                        </h3>
                    </div>
                    <ul className="divide-y divide-brand-green/10">
                        {stocks.map((s) => (
                            <StockRow
                                key={s.product_id}
                                stock={s}
                                onSet={handleSet}
                                onAdjust={handleAdjust}
                                adjustingId={adjustingId}
                                setAdjustingId={setAdjustingId}
                            />
                        ))}
                    </ul>
                </div>
            )}

            {showMovements && (
                <div className="rounded-2xl bg-white shadow-soft ring-1 ring-brand-green/10 overflow-hidden">
                    <div className="px-6 py-4 border-b border-brand-green/10">
                        <h3 className="font-display font-medium text-lg text-brand-ink">
                            Movimentos de stock ({movements.length})
                        </h3>
                    </div>
                    {movements.length === 0 ? (
                        <div className="p-12 text-center text-sm text-brand-ink-soft">
                            Sem movimentos registados.
                        </div>
                    ) : (
                        <ul className="divide-y divide-brand-green/10 max-h-[500px] overflow-y-auto">
                            {movements.map((m) => (
                                <li
                                    key={m.id}
                                    data-testid={`stock-movement-${m.id}`}
                                    className="px-6 py-3 flex items-center justify-between gap-4 text-sm"
                                >
                                    <div>
                                        <div className="font-semibold text-brand-ink">{m.product_name}</div>
                                        <div className="text-xs text-brand-ink-soft">
                                            {m.reason.replace(/_/g, " ")}
                                            {m.note ? ` · ${m.note}` : ""}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div
                                            className={`font-bold ${
                                                m.delta > 0 ? "text-brand-green" : "text-red-500"
                                            }`}
                                        >
                                            {m.delta > 0 ? "+" : ""}
                                            {m.delta}
                                        </div>
                                        <div className="text-[11px] text-brand-ink-soft">
                                            {formatDateTime(m.created_at)}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

function StockRow({ stock, onSet, onAdjust, adjustingId, setAdjustingId }) {
    const [qty, setQty] = useState(String(stock.quantity));
    const [threshold, setThreshold] = useState(String(stock.low_threshold));
    const [saving, setSaving] = useState(false);
    const [adjDelta, setAdjDelta] = useState("1");
    const [adjReason, setAdjReason] = useState("reposicao");
    const [adjNote, setAdjNote] = useState("");

    useEffect(() => {
        setQty(String(stock.quantity));
        setThreshold(String(stock.low_threshold));
    }, [stock.quantity, stock.low_threshold]);

    const isAdjusting = adjustingId === stock.product_id;

    const saveSet = async () => {
        setSaving(true);
        await onSet(stock.product_id, {
            quantity: parseInt(qty || "0", 10),
            low_threshold: parseInt(threshold || "0", 10),
        });
        setSaving(false);
    };

    const submitAdjust = async (e) => {
        e.preventDefault();
        const d = parseInt(adjDelta || "0", 10);
        if (!d) {
            toast.error("Indica uma quantidade diferente de zero");
            return;
        }
        await onAdjust(stock.product_id, d, adjReason, adjNote);
        setAdjDelta("1");
        setAdjReason("reposicao");
        setAdjNote("");
    };

    return (
        <li
            data-testid={`stock-row-${stock.product_id}`}
            className={`px-6 py-4 flex flex-col gap-3 md:flex-row md:items-center md:gap-4 ${
                stock.is_low ? "bg-red-50/40" : ""
            }`}
        >
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <div
                    className="h-14 w-14 rounded-2xl bg-brand-cream flex items-center justify-center overflow-hidden flex-shrink-0"
                    style={{ background: stock.color ? `${stock.color}33` : undefined }}
                >
                    {stock.image ? (
                        <img
                            src={stock.image}
                            alt={stock.name}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                                e.currentTarget.style.display = "none";
                            }}
                        />
                    ) : (
                        <Package className="h-6 w-6 text-brand-ink-soft" />
                    )}
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-brand-ink truncate">{stock.name}</span>
                        <span
                            className={`text-[10px] uppercase tracking-widest font-bold rounded-full px-2 py-0.5 ${
                                stock.type === "kit"
                                    ? "bg-brand-orange-soft text-brand-orange"
                                    : "bg-brand-green-soft text-brand-green"
                            }`}
                        >
                            {stock.type}
                        </span>
                        {stock.is_low && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold rounded-full bg-red-100 text-red-700 px-2 py-0.5">
                                <AlertTriangle className="h-3 w-3" /> Stock baixo
                            </span>
                        )}
                    </div>
                    <div className="mt-0.5 text-xs text-brand-ink-soft">
                        {formatPrice(stock.price)} · atualizado {formatDateTime(stock.updated_at) || "—"}
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <NumField label="Qtd" value={qty} onChange={setQty} testid={`stock-qty-${stock.product_id}`} />
                <NumField
                    label="Alerta <="
                    value={threshold}
                    onChange={setThreshold}
                    testid={`stock-threshold-${stock.product_id}`}
                />
                <button
                    type="button"
                    onClick={saveSet}
                    disabled={saving}
                    data-testid={`stock-save-${stock.product_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-60"
                >
                    {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Guardar
                </button>
                <button
                    type="button"
                    onClick={() => setAdjustingId(isAdjusting ? null : stock.product_id)}
                    data-testid={`stock-adjust-toggle-${stock.product_id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-ink hover:bg-brand-green-dark text-white px-3 py-2 text-xs font-semibold"
                >
                    {isAdjusting ? <X className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
                    Ajustar
                </button>
            </div>

            {isAdjusting && (
                <form
                    onSubmit={submitAdjust}
                    data-testid={`stock-adjust-form-${stock.product_id}`}
                    className="basis-full mt-2 bg-white rounded-2xl ring-1 ring-brand-green/10 p-4 grid grid-cols-1 md:grid-cols-5 gap-3 items-end"
                >
                    <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft block mb-1">
                            Variação (+/-)
                        </label>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => setAdjDelta(String(-Math.abs(parseInt(adjDelta || "0", 10) || 1)))}
                                className="h-10 w-10 rounded-full bg-red-50 text-red-600 hover:bg-red-100"
                                data-testid={`stock-adjust-minus-${stock.product_id}`}
                            >
                                <Minus className="h-4 w-4 mx-auto" />
                            </button>
                            <input
                                type="number"
                                value={adjDelta}
                                onChange={(e) => setAdjDelta(e.target.value)}
                                data-testid={`stock-adjust-delta-${stock.product_id}`}
                                className="flex-1 rounded-2xl border-2 border-brand-green/15 bg-white px-3 py-2 text-sm text-center font-semibold text-brand-ink focus:outline-none focus:border-brand-green"
                            />
                            <button
                                type="button"
                                onClick={() => setAdjDelta(String(Math.abs(parseInt(adjDelta || "0", 10) || 1)))}
                                className="h-10 w-10 rounded-full bg-brand-green-soft text-brand-green hover:bg-brand-green hover:text-white transition-colors"
                                data-testid={`stock-adjust-plus-${stock.product_id}`}
                            >
                                <Plus className="h-4 w-4 mx-auto" />
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft block mb-1">
                            Razão
                        </label>
                        <select
                            value={adjReason}
                            onChange={(e) => setAdjReason(e.target.value)}
                            data-testid={`stock-adjust-reason-${stock.product_id}`}
                            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                        >
                            <option value="reposicao">Reposição</option>
                            <option value="perda">Perda / quebra</option>
                            <option value="devolucao">Devolução</option>
                            <option value="correcao">Correção</option>
                            <option value="outro">Outro</option>
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft block mb-1">
                            Nota (opcional)
                        </label>
                        <input
                            type="text"
                            value={adjNote}
                            onChange={(e) => setAdjNote(e.target.value)}
                            data-testid={`stock-adjust-note-${stock.product_id}`}
                            className="w-full rounded-2xl border-2 border-brand-green/15 bg-white px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-green"
                        />
                    </div>
                    <button
                        type="submit"
                        data-testid={`stock-adjust-submit-${stock.product_id}`}
                        className="inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-4 py-2.5 text-xs font-semibold transition-colors"
                    >
                        Registar movimento
                    </button>
                </form>
            )}
        </li>
    );
}

const NumField = ({ label, value, onChange, testid }) => (
    <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft block mb-0.5">
            {label}
        </label>
        <input
            type="number"
            min="0"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            className="w-20 rounded-2xl border-2 border-brand-green/15 bg-white px-3 py-2 text-sm font-semibold text-center text-brand-ink focus:outline-none focus:border-brand-green"
        />
    </div>
);

const Stat = ({ icon: Icon, label, value, accent, bg, white, testid }) => (
    <div
        data-testid={testid}
        className={`rounded-2xl ${bg} p-5 md:p-6 shadow-soft ring-1 ring-brand-green/5`}
    >
        <Icon className={`h-5 w-5 ${accent}`} />
        <div
            className={`mt-3 text-[11px] uppercase tracking-widest font-bold ${
                white ? "text-white/80" : "text-brand-ink-soft"
            }`}
        >
            {label}
        </div>
        <div
            className={`mt-1 font-display font-medium text-3xl md:text-4xl ${
                white ? "text-white" : "text-brand-ink"
            }`}
        >
            {value}
        </div>
    </div>
);
